<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { SliderRange, SliderRoot, SliderThumb, SliderTrack } from 'radix-vue'

import ChipsCount from '../shared/ChipsCount.vue'

import GameButton from '@/components/shared/GameButton.vue'
import UiInput from '@/components/shared/UiInput.vue'

import { useGameState } from '@/stores/gameState'
import { useUiState } from '@/stores/uiState'

import { useInHandActions } from '@/composables/useInHandActions'

interface Props {
  canAct?: boolean
  isShowdown?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  canAct: true,
  isShowdown: false
})

const { isPending, bet, raise, fold, call, check } = useInHandActions()

const {
  roomState,
  isMyTurn,
  autoAction,
  callAmount,

  legalRaiseRange,
  legalActions,
  raiseLevels,
  winners,

  player,
} = storeToRefs(useGameState())
const { requestAutoAction, sitIn } = useGameState()
const uiState = useUiState()

const isPlayerSittingOut = computed(() => player.value?.states?.includes('sit_out'))

// actions
const isCheck = computed(() => legalActions.value.includes('check'))
const isCall = computed(() => legalActions.value.includes('call'))
const isBet = computed(() => legalActions.value.includes('bet'))
const betRaiseActionList = computed(() => [...raiseLevels.value].sort((a, b) => a.amount - b.amount))
const isBetOrRaiseAllowed = computed(
  () => legalActions.value.includes('bet') || legalActions.value.includes('raise')
)
function betOrRaise(amount: number): void {
  isBet.value ? bet(amount) : raise(amount)
}

// raise amount
const raiseAmount = ref(legalRaiseRange.value.min)

watch(legalRaiseRange, () => {
  raiseAmount.value = legalRaiseRange.value.min
})

function setRaiseAmount(amount: number) {
  raiseAmount.value = amount
}

// slider + input
const sliderStep = computed(() => roomState.value.gameConfig?.blinds[1] ?? 1)
const sliderValue = computed({
  get() {
    const result = [raiseAmount.value]
    return result as number[]
  },
  set([value]: number[]) {
    raiseAmount.value = value
  }
})

const inputValue = computed({
  get() {
    return raiseAmount.value.toString()
  },
  set(val: string) {
    const value = Number(val)
    if (value < legalRaiseRange.value.min) {
      raiseAmount.value = legalRaiseRange.value.min
    } else if (value > legalRaiseRange.value.max) {
      raiseAmount.value = legalRaiseRange.value.max
    } else {
      raiseAmount.value = value
    }
  }
})

const minus = () => {
  const min = legalRaiseRange.value.min
  const newValue = raiseAmount.value - sliderStep.value
  raiseAmount.value = newValue <= min ? min : newValue
}

const plus = () => {
  const max = legalRaiseRange.value.max
  const newValue = raiseAmount.value + sliderStep.value
  raiseAmount.value = newValue >= max ? max : newValue
}

const isMinusDisabled = computed(() => {
  return raiseAmount.value <= legalRaiseRange.value.min
})

const isPlusDisabled = computed(() => {
  return raiseAmount.value >= legalRaiseRange.value.max
})

const handleSitIn = () => {
  sitIn();
  if (props.isShowdown) return;
  uiState.modals.attention.open({
    titleKey: 'TableSitInAttentionModal.label.Title',
    descriptionKey: 'TableSitInAttentionModal.label.Description',
    buttonKey: 'TableSitInAttentionModal.button.Okay'
  })
}
</script>

<template>
  <div class="hand-actions">
    <template v-if="props.canAct">
      <div v-if="isMyTurn && isBetOrRaiseAllowed" class="hand-actions__raise-controls">
        <ul class="hand-actions__raise-buttons">
          <li v-for="lvl in betRaiseActionList" :key="lvl.factor" class="hand-actions__raise-button">
            <button :disabled="lvl.amount < legalRaiseRange.min" @click="setRaiseAmount(lvl.amount)">
              {{ lvl.factor * 100 }}%
            </button>
          </li>
        </ul>
        <div class="hand-actions__raise-slider-row">
          <div class="hand-actions__raise-slider-area">
            <button :disabled="isMinusDisabled" class="hand-actions__raise-slider-button" @click="minus">
              -
            </button>
            <div class="hand-actions__raise-slider-wrap">
              <slider-root v-model="sliderValue" class="hand-actions__raise-slider" :min="legalRaiseRange.min"
                :max="legalRaiseRange.max" :step="sliderStep">
                <slider-track class="hand-actions__raise-slider-track">
                  <slider-range class="hand-actions__raise-slider-range" />
                </slider-track>
                <SliderThumb class="hand-actions__raise-slider-thumb" aria-label="Volume" />
              </slider-root>
            </div>
            <button :disabled="isPlusDisabled" class="hand-actions__raise-slider-button" @click="plus">
              +
            </button>
          </div>
          <ui-input id="raiseInput" v-model="inputValue" placeholder="Stack" wrapper-class="hand-actions__raise-input"
            inner-class="hand-actions__raise-input-inner" class="hand-actions__raise-input-field" :mask="{
              mask: '9 99#',
              reversed: true,
              tokens: { '9': { pattern: /[0-9]/, repeated: true } }
            }" />
        </div>
      </div>

      <game-button v-if="!isMyTurn && !isShowdown" :disabled="isPending" variant="secondary-auto"
        :auto-action-active="autoAction === 'fold'" @click="requestAutoAction('fold')">
        {{
          callAmount === 0
            ? `${$t('PokerTable.button.Check')} / ${$t('PokerTable.button.Fold')}`
            : $t('PokerTable.button.Fold')
        }}
      </game-button>
      <game-button id="fold" v-if="isMyTurn" :disabled="isPending" variant="primary" @click="fold">
        {{ $t('PokerTable.button.Fold') }}
      </game-button>

      <game-button v-if="!isMyTurn && !isShowdown && !callAmount" :disabled="isPending" variant="secondary-auto"
        :auto-action-active="autoAction === 'check'" @click="requestAutoAction('check')">
        {{ $t('PokerTable.button.Check') }}
      </game-button>
      <template v-if="isMyTurn">
        <game-button id="check" v-if="isCheck" :disabled="isPending" variant="primary" @click="check">
          {{ $t('PokerTable.button.Check') }}
        </game-button>
        <game-button id="callOrAllIn" v-if="isCall" :disabled="isPending" variant="primary" @click="call">
          <template v-if="callAmount >= legalRaiseRange.max">
            {{ $t('PokerTable.button.AllIn') }}
          </template>
          <template v-else>
            {{ $t('PokerTable.button.Call') }}&nbsp;<chips-count :value="callAmount" class="hand-actions__count" />
          </template>
        </game-button>
      </template>

      <game-button id="betOrRaiseOrAllIn" v-if="isMyTurn && callAmount < legalRaiseRange.max && isBetOrRaiseAllowed"
        :disabled="isPending" variant="primary" :class="{
          'hand-actions--hidden': !isMyTurn || callAmount >= legalRaiseRange.max
        }" @click="betOrRaise(raiseAmount)">
        <template v-if="raiseAmount === legalRaiseRange.max">
          {{ $t('PokerTable.button.AllIn') }}
        </template>
        <template v-else-if="isBet">
          {{ $t('PokerTable.button.Bet') }}&nbsp;<chips-count :value="raiseAmount" class="hand-actions__count" />
        </template>
        <template v-else>
          {{ $t('PokerTable.button.RaiseTo') }}&nbsp;<chips-count :value="raiseAmount" class="hand-actions__count" />
        </template>
      </game-button>
      <game-button v-if="!isMyTurn" class="hand-actions--hidden" />
    </template>

    <game-button v-if="isPlayerSittingOut" :disabled="isPending" variant="primary" @click="handleSitIn">
      {{ $t('PokerTable.button.SitIn') }}
    </game-button>
  </div>
</template>

<style lang="scss">
.hand-actions {
  position: relative;
  display: flex;
  gap: 10px;

  &--hidden {
    visibility: hidden;
  }

  &__raise-controls {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 6px;
    position: absolute;
    right: 0;
    width: 340px;
    bottom: 100%;

    @media (max-height: 768px) {
      flex-direction: row !important;
      width: 100% !important;
    }
  }

  &__raise-buttons {
    list-style: none;
    width: 100%;
    margin: 0;
    padding: 0;

    background: #22242e;
    border-radius: 9px;
    border: 1.5px solid rgba(64, 66, 77, 0.5);

    display: flex;
    flex-direction: row;
    height: 32px;
  }

  &__raise-button {
    flex: 1;
    color: #fff;
    font-weight: 500;
    letter-spacing: 0.167px;
    cursor: pointer;

    &:not(:last-child) {
      border-right: 1px solid rgba(64, 66, 77, 0.5);
    }

    button {
      width: 100%;
      height: 100%;
      background-color: transparent;
      border: none;
      outline: none;

      display: flex;
      align-items: center;
      justify-content: center;

      color: #fff;
      font-weight: 500;
      letter-spacing: 0.167px;
      cursor: pointer;

      &:disabled {
        color: gray;
        cursor: not-allowed;
      }
    }
  }

  &__raise-slider-row {
    height: 32px;
    width: 100%;
    margin: 0;
    padding: 0;

    display: flex;
    flex-direction: row;
    gap: 12px;
  }

  &__raise-slider-area {
    border-radius: 9px;
    border: 1.5px solid rgba(64, 66, 77, 0.5);
    background: #22242e;
    flex: 2;

    display: flex;
    flex-direction: row;
    overflow: hidden;
  }

  &__raise-slider-button {
    margin: 0;
    width: 30px;
    height: 100%;
    background-color: transparent;
    border: none;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #fff;
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
      color: gray;
      cursor: not-allowed;
    }
  }

  &__raise-slider-wrap {
    border-right: 1px solid rgba(64, 66, 77, 0.5);
    border-left: 1px solid rgba(64, 66, 77, 0.5);
    display: flex;
    flex: 1;

    align-items: center;
    justify-content: center;
  }

  &__raise-slider {
    width: 80%;
    height: 100%;
    position: relative;

    display: flex;
    align-items: center;
    justify-content: center;

    &-track {
      position: relative;
      height: 4px;
      border-radius: 2px;
      width: 100%;
      background: #6c696f;
    }

    &-range {
      position: absolute;
      height: 100%;
      border-radius: 6px;
      background: linear-gradient(270deg, #ffd5b7 -119.53%, #d9a452 118.75%);
    }

    &-thumb {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background-color: white;
    }
  }

  &__raise-input {
    flex: 1;
    height: 100%;

    &-inner {
      height: 100%;
      padding: 0;
      border-radius: 6px;
      border: 2px solid rgba(87, 87, 95, 0.44);
      opacity: 0.9;
      background: linear-gradient(180deg,
          #0c0c0f 0.37%,
          #08080a 35.93%,
          #040405 99.63%);
    }
  }

  &__count {
    font-weight: 600;
  }
}
</style>
